/**
 * Evéllae — compact variant picker on product cards (sizes + colors) with add to cart.
 * Markup comes from blocks/card-variants.liquid.
 */
import { CartAddEvent } from '@theme/events';

const COLOR = 'color';
const SIZE = 'size';

class CardVariants extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    try {
      this.variants = JSON.parse(this.querySelector('[data-cv-json]')?.textContent || '[]');
    } catch (e) {
      this.variants = [];
    }
    this.rows = Array.from(this.querySelectorAll('[data-cv-row]'));
    this.addBtn = this.querySelector('[data-cv-add]');
    this.label = this.querySelector('[data-cv-label]');
    this.selected = this.rows.map((row) => {
      const pre = row.querySelector('[aria-pressed="true"]');
      return pre ? pre.dataset.value : null;
    });
    this.addEventListener('click', this.onClick);
    this.refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this._ready = false;
  }

  /** Index of an option row inside the product's option list */
  indexOf(row) {
    return Number(row.dataset.index);
  }

  matches(variant, selection) {
    return this.rows.every((row, i) => {
      const value = selection[i];
      return value == null || variant.o[this.indexOf(row)] === value;
    });
  }

  currentVariant() {
    if (this.selected.some((v) => v == null)) return null;
    return this.variants.find((v) => this.matches(v, this.selected)) || null;
  }

  refresh() {
    // Availability of every value given the other selections
    this.rows.forEach((row, i) => {
      row.querySelectorAll('[data-value]').forEach((btn) => {
        const trial = this.selected.slice();
        trial[i] = btn.dataset.value;
        const ok = this.variants.some((v) => v.a && this.matches(v, trial));
        btn.classList.toggle('is-unavailable', !ok);
        btn.setAttribute('aria-pressed', String(this.selected[i] === btn.dataset.value));
      });
    });

    const variant = this.currentVariant();
    const interacted = this.dataset.touched === 'true' || this.rows.every((r) => r.hidden);
    if (!this.addBtn) return;
    if (variant && variant.a) {
      this.addBtn.disabled = false;
      this.setLabel(this.dataset.labelAdd);
    } else if (variant && !variant.a) {
      this.addBtn.disabled = true;
      this.setLabel(this.dataset.labelSoldOut);
    } else {
      this.addBtn.disabled = true;
      this.setLabel(this.dataset.labelChoose);
    }
    this.toggleAttribute('data-open', interacted);
  }

  setLabel(text) {
    if (this.label && text) this.label.textContent = text;
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const opt = target.closest('[data-value]');
    const add = target.closest('[data-cv-add]');
    if (!opt && !add) return;
    event.preventDefault();
    event.stopPropagation();

    if (opt) {
      if (this.busy) return;
      const row = opt.closest('[data-cv-row]');
      const i = this.rows.indexOf(row);
      if (i < 0) return;
      const value = opt.dataset.value;
      if (opt.classList.contains('is-unavailable') && row.dataset.kind !== COLOR) return;
      this.selected[i] = value;
      // A color whose current size combo is unavailable: drop the size so the shopper can re-pick
      if (row.dataset.kind === COLOR) {
        this.rows.forEach((r, j) => {
          if (j !== i && this.selected[j] != null && !this.variants.some((v) => v.a && this.matches(v, this.selected))) {
            if (r.dataset.kind === SIZE) this.selected[j] = null;
          }
        });
        this.showColor(i, value);
      }
      this.dataset.touched = 'true';
      this.refresh();
      return;
    }

    if (add && !add.disabled && !this.busy) this.addToCart();
  };

  /** Swap the card image to the chosen color when the gallery has it */
  showColor(index, value) {
    const card = this.closest('product-card');
    const trial = this.selected.slice();
    trial[index] = value;
    const variant =
      this.variants.find((v) => v.a && this.matches(v, trial) && v.m) ||
      this.variants.find((v) => v.o[this.indexOf(this.rows[index])] === value && v.m);
    if (!card || !variant) return;
    try {
      const slideshow = card.refs && card.refs.slideshow;
      const id = String(variant.m);
      const slides = slideshow?.refs?.slides || [];
      if (!slides.some((s) => s.getAttribute('slide-id') === id)) return;
      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;
        slide.hidden = slide.getAttribute('slide-id') !== id;
      }
      slideshow.select({ id }, undefined, { animate: false });
    } catch (e) {
      /* the image simply stays as it is */
    }
  }

  async addToCart() {
    const variant = this.currentVariant();
    if (!variant || !variant.a || this.busy) return;
    this.busy = true;
    this.addBtn.disabled = true;
    this.setLabel(this.dataset.labelAdding);

    const routes = (window.Theme && window.Theme.routes) || {};
    const addUrl = `${routes.cart_add_url || '/cart/add'}.js`;
    const cartUrl = `${routes.cart_url || '/cart'}.js`;
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], ...(sections ? { sections } : {}) }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Error');

      let cart;
      try {
        cart = await (await fetch(cartUrl, { headers: { Accept: 'application/json' } })).json();
      } catch (e) {
        cart = undefined;
      }

      this.dispatchEvent(
        new CartAddEvent(cart, String(variant.id), {
          source: 'card-variants',
          itemCount: 1,
          productId: this.dataset.productId,
          variantId: String(variant.id),
          sections: data.sections,
        })
      );
      this.setLabel(this.dataset.labelAdded);
      this.classList.add('is-added');
      setTimeout(() => {
        this.classList.remove('is-added');
        this.busy = false;
        this.refresh();
      }, 1800);
    } catch (error) {
      this.setLabel(error && error.message ? error.message : this.dataset.labelSoldOut);
      setTimeout(() => {
        this.busy = false;
        this.refresh();
      }, 2600);
    }
  }
}

if (!customElements.get('card-variants')) customElements.define('card-variants', CardVariants);
